import React, { useEffect, useState } from 'react'
import { Table, Button, Form, Input, Card, Row, Col, Modal } from 'antd'
import {
  FileTextOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import {
  GetTermsConditionData,
  CreateTermsConditionData,
  UpdateTermsConditionData,
  DeleteTermsConditionData,
} from "../../API's/AdminApi"
import { toast } from 'react-toastify'

const { TextArea } = Input

export default function TermsAndConditionMaster() {
  const [data, setData] = useState([])
  const [showForm, setShowForm] = useState(false)
  const [editData, setEditData] = useState(null)
  const [deleteId, setDeleteId] = useState(null)

  const [tableLoading, setTableLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [searchText, setSearchText] = useState('')
    const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  const [form] = Form.useForm()

  // ---------------- GET DATA ----------------
  const getTerms = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetTermsConditionData)

      const formattedData = Array.isArray(res.data) ? res.data : []
      setData(formattedData)
    } catch (error) {
      toast.error('Failed to load terms')
    } finally {
      setTableLoading(false)
    }
  }

  useEffect(() => {
    getTerms()
  }, [])

  // ---------------- ADD / UPDATE ----------------
  const onFinish = async (values) => {
    try {
      setSubmitLoading(true)
      let res

      if (editData?.id) {
        // UPDATE
        res = await axiosInstance.put(UpdateTermsConditionData, {
          id: editData.id,
          termsCondition: values.content,
        })

        if (res.data === 'UPDATED') {
          toast.success('Terms updated successfully')
        } else if (res.data === 'ALREADYEXISTS') {
          toast.warning('Terms already exist')
        }
      } else {
        // CREATE
        res = await axiosInstance.post(CreateTermsConditionData, {
          termsCondition: values.content,
        })

        if (res.data === 'SAVED') {
          toast.success('Terms created successfully')
        } else if (res.data === 'ALREADYEXISTS') {
          toast.warning('Terms already exist')
        }
      }

      form.resetFields()
      setShowForm(false)
      setEditData(null)
      getTerms()
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Something went wrong')
    } finally {
      setSubmitLoading(false)
    }
  }

  // ---------------- EDIT ----------------
  const handleEdit = (record) => {
    setEditData(record)
    setShowForm(true)

    form.setFieldsValue({
      content: record.termsCondition,
    })
  }

  // ---------------- DELETE ----------------
  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      const res = await axiosInstance.delete(
        `${DeleteTermsConditionData}?id=${deleteId}`
      )

      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteId(null)
        getTerms()
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete')
    } finally {
      setDeleteLoading(false)
    }
  }

  // ---------------- TABLE COLUMNS ----------------
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Terms Content',
      render: (_, record) => (
        <span>
          {record.termsCondition
            ? record.termsCondition.slice(0, 120) + '...'
            : ''}
        </span>
      ),
    },
    {
      title: 'Action',
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ marginRight: 8 }}
          />

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => setDeleteId(record.id)}
          />
        </>
      ),
    },
  ]

  // ---------------- SEARCH ----------------
  const filteredData = data.filter((item) =>
    item.termsCondition
      ?.toLowerCase()
      .includes(searchText.toLowerCase())
  )

  return (
    <div>
      {/* TABLE VIEW */}
      {!showForm && (
        <div className="page-background p-2 p-md-4">
          <div className="page-header">
            <h2>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Terms & Conditions Master
            </h2>
          </div>

          <Row gutter={16} className="pb-2 m-0">
            <Col xs={24} md={6} xl={14}></Col>

            <Col xs={24} md={12} xl={6} className="pt-2 pt-md-0">
              <Input
                placeholder="Search here"
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>

            <Col xs={24} md={6} xl={4} className="pt-2 pt-md-0 mt-1">
              <button
                className="btn-aqua"
                onClick={() => {
                  setShowForm(true)
                  setEditData(null)
                  form.resetFields()
                }}
              >
                <PlusOutlined /> Add Terms
              </button>
            </Col>
          </Row>

          <Table
            className="custom-table-header shadow"
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            bordered
            loading={tableLoading}
            style={{ overflowX: 'auto', backgroundColor: 'white' }}
            pagination={{
                showSizeChanger: true, // enable page size dropdown
                pageSizeOptions: ['5', '10', '20', '50'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
          />
        </div>
      )}

      {/* FORM VIEW */}
      {showForm && (
        <div className="row p-0 m-0">
          <div className="col-12 col-md-2 col-lg-3"></div>

          <div className="col-12 col-md-8 col-lg-6 page-background1 p-2 p-md-4">
            <div className="page-header">
              <h2>
                <FileTextOutlined style={{ marginRight: 8 }} />
                Terms & Conditions Master
              </h2>
            </div>

            <Card style={{ backgroundColor: 'transparent' }}>
              <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item
                  label="Terms Condition Content"
                  name="content"
                  rules={[
                    { required: true, message: 'Enter terms content' },
                  ]}
                >
                  <TextArea
                    rows={6}
                    placeholder="Enter Terms Condition Content"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={submitLoading}
                  >
                    {editData ? 'Update' : 'Submit'}
                  </Button>

                  <Button
                    style={{ marginLeft: 10 }}
                    onClick={() => {
                      setShowForm(false)
                      setEditData(null)
                      form.resetFields()
                    }}
                  >
                    Cancel
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </div>

          <div className="col-12 col-md-2 col-lg-3"></div>
        </div>
      )}

      {/* DELETE MODAL */}
      <Modal
        title="Confirm Delete"
        open={deleteId !== null}
        onOk={confirmDelete}
        confirmLoading={deleteLoading}
        onCancel={() => setDeleteId(null)}
      >
        Are you sure you want to delete this Terms & Condition?
      </Modal>
    </div>
  )
}